import { Tool } from '@modelcontextprotocol/sdk/types.js';
import { ValidateConventionsInputSchema, type Config, type ValidationResult } from '../types/index.js';
import { findFiles, readText } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const validateConventionsTool: Tool = {
  name: 'validate_conventions',
  description: 'Validate AtlasHub/SmartStack conventions: SQL schemas (core/extensions), domain table prefixes (auth_, nav_, ai_, etc.), migration naming ({context}_v{version}_{sequence}_*), service interfaces (I*Service), namespace structure, controller routes (NavRoute)',
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to validate (default: SmartStack.app path from config)',
      },
      checks: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['tables', 'migrations', 'services', 'namespaces', 'entities', 'tenants', 'controllers', 'layouts', 'tabs', 'hierarchies', 'protected-actions', 'all'],
        },
        description: 'Types of checks to perform',
        default: ['all'],
      },
    },
  },
};

export async function handleValidateConventions(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ValidateConventionsInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;
  const checks = input.checks.includes('all')
    ? ['tables', 'migrations', 'services', 'namespaces', 'entities', 'tenants', 'controllers', 'layouts', 'tabs', 'hierarchies', 'protected-actions']
    : input.checks;

  logger.info('Validating conventions', { projectPath, checks });

  const result: ValidationResult = {
    valid: true,
    errors: [],
    warnings: [],
    summary: '',
  };

  const structure = await findSmartStackStructure(projectPath);

  // Run selected checks
  if (checks.includes('tables')) {
    await validateTablePrefixes(structure, config, result);
  }

  if (checks.includes('migrations')) {
    await validateMigrationNaming(structure, config, result);
  }

  if (checks.includes('services')) {
    await validateServiceInterfaces(structure, config, result);
  }

  if (checks.includes('namespaces')) {
    await validateNamespaces(structure, config, result);
  }

  if (checks.includes('entities')) {
    await validateEntities(structure, config, result);
  }

  if (checks.includes('tenants')) {
    await validateTenantAwareness(structure, config, result);
  }

  if (checks.includes('controllers')) {
    await validateControllerRoutes(structure, config, result);
  }

  if (checks.includes('layouts')) {
    await validateLayouts(structure, config, result);
  }

  if (checks.includes('tabs')) {
    await validateTabs(structure, config, result);
  }

  if (checks.includes('hierarchies')) {
    await validateHierarchies(structure, config, result);
  }

  if (checks.includes('protected-actions')) {
    await validateProtectedActions(structure, config, result);
  }

  // Update validity
  result.valid = result.errors.length === 0;

  // Generate summary
  result.summary = generateSummary(result, checks);

  return formatResult(result);
}

async function validateTablePrefixes(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.infrastructure) {
    result.warnings.push({
      type: 'warning',
      category: 'tables',
      message: 'Infrastructure project not found, skipping table validation',
    });
    return;
  }

  // Find EF Core configuration files
  const configFiles = await findFiles('**/Configurations/**/*.cs', {
    cwd: structure.infrastructure,
  });

  const validSchemas = [config.conventions.schemas.platform, config.conventions.schemas.extensions];
  const validPrefixes = config.conventions.tablePrefixes;

  // Map SchemaConstants to actual values
  const schemaConstantsMap: Record<string, string> = {
    'SchemaConstants.Core': config.conventions.schemas.platform,
    'SchemaConstants.Extensions': config.conventions.schemas.extensions,
  };

  for (const file of configFiles) {
    const content = await readText(file);

    // Match ToTable with string schema: .ToTable("tableName", "schemaName")
    const tableWithStringSchemaMatches = content.matchAll(/\.ToTable\s*\(\s*"([^"]+)"\s*,\s*"([^"]+)"\s*\)/g);

    for (const match of tableWithStringSchemaMatches) {
      const tableName = match[1];
      const schemaName = match[2];

      // Validate schema
      if (!validSchemas.includes(schemaName)) {
        result.errors.push({
          type: 'error',
          category: 'tables',
          message: `Table "${tableName}" uses invalid schema "${schemaName}"`,
          file: path.relative(structure.root, file),
          suggestion: `Use schema "${config.conventions.schemas.platform}" for SmartStack tables or "${config.conventions.schemas.extensions}" for client extensions`,
        });
      }

      // Validate table prefix (must have a domain prefix)
      const hasValidPrefix = validPrefixes.some(prefix => tableName.startsWith(prefix));
      if (!hasValidPrefix && !tableName.startsWith('__')) { // Ignore EF migrations history
        result.warnings.push({
          type: 'warning',
          category: 'tables',
          message: `Table "${tableName}" does not use a standard domain prefix`,
          file: path.relative(structure.root, file),
          suggestion: `Consider using a domain prefix: ${validPrefixes.slice(0, 5).join(', ')}, etc.`,
        });
      }
    }

    // Match ToTable with SchemaConstants: .ToTable("tableName", SchemaConstants.Core)
    const tableWithConstantSchemaMatches = content.matchAll(/\.ToTable\s*\(\s*"([^"]+)"\s*,\s*(SchemaConstants\.\w+)\s*\)/g);

    for (const match of tableWithConstantSchemaMatches) {
      const tableName = match[1];
      const schemaConstant = match[2];
      const resolvedSchema = schemaConstantsMap[schemaConstant];

      // Validate schema constant is known
      if (!resolvedSchema) {
        result.errors.push({
          type: 'error',
          category: 'tables',
          message: `Table "${tableName}" uses unknown schema constant "${schemaConstant}"`,
          file: path.relative(structure.root, file),
          suggestion: `Use SchemaConstants.Core for SmartStack tables or SchemaConstants.Extensions for client extensions`,
        });
      }

      // Validate table prefix (must have a domain prefix)
      const hasValidPrefix = validPrefixes.some(prefix => tableName.startsWith(prefix));
      if (!hasValidPrefix && !tableName.startsWith('__')) { // Ignore EF migrations history
        result.warnings.push({
          type: 'warning',
          category: 'tables',
          message: `Table "${tableName}" does not use a standard domain prefix`,
          file: path.relative(structure.root, file),
          suggestion: `Consider using a domain prefix: ${validPrefixes.slice(0, 5).join(', ')}, etc.`,
        });
      }
    }

    // Check for ToTable without schema (old format) - but exclude lines with SchemaConstants
    const tableWithoutSchemaMatches = content.matchAll(/\.ToTable\s*\(\s*"([^"]+)"\s*\)(?!\s*,)/g);

    for (const match of tableWithoutSchemaMatches) {
      const tableName = match[1];
      if (!tableName.startsWith('__')) { // Ignore EF migrations history
        result.errors.push({
          type: 'error',
          category: 'tables',
          message: `Table "${tableName}" is missing schema specification`,
          file: path.relative(structure.root, file),
          suggestion: `Add schema: .ToTable("${tableName}", SchemaConstants.Core)`,
        });
      }
    }
  }
}

async function validateMigrationNaming(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.migrations) {
    result.warnings.push({
      type: 'warning',
      category: 'migrations',
      message: 'Migrations folder not found, skipping migration validation',
    });
    return;
  }

  const migrationFiles = await findFiles('*.cs', { cwd: structure.migrations });

  // Expected format: {context}_v{version}_{sequence}_{Description}.cs
  // Example: core_v1.0.0_001_CreateAuthUsers.cs
  const migrationPattern = /^(\w+)_v(\d+\.\d+\.\d+)_(\d{3})_(.+)\.cs$/;
  const designerPattern = /\.Designer\.cs$/;

  for (const file of migrationFiles) {
    const fileName = path.basename(file);

    // Skip designer files and snapshot
    if (designerPattern.test(fileName) || fileName.includes('ModelSnapshot')) {
      continue;
    }

    if (!migrationPattern.test(fileName)) {
      result.errors.push({
        type: 'error',
        category: 'migrations',
        message: `Migration "${fileName}" does not follow naming convention`,
        file: path.relative(structure.root, file),
        suggestion: `Expected format: {context}_v{version}_{sequence}_{Description}.cs (e.g., core_v1.0.0_001_CreateAuthUsers.cs)`,
      });
    }
  }

  // Check version and sequence order
  const orderedMigrations = migrationFiles
    .map(f => path.basename(f))
    .filter(f => migrationPattern.test(f) && !f.includes('Designer'))
    .sort();

  for (let i = 1; i < orderedMigrations.length; i++) {
    const prev = orderedMigrations[i - 1];
    const curr = orderedMigrations[i];

    const prevMatch = migrationPattern.exec(prev);
    const currMatch = migrationPattern.exec(curr);

    if (prevMatch && currMatch) {
      const prevVersion = prevMatch[2];
      const currVersion = currMatch[2];

      // Check version ordering (semver comparison)
      if (currVersion < prevVersion) {
        result.warnings.push({
          type: 'warning',
          category: 'migrations',
          message: `Migration order issue: "${curr}" (v${currVersion}) comes before "${prev}" (v${prevVersion})`,
        });
      }
    }
  }
}

async function validateServiceInterfaces(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.application) {
    result.warnings.push({
      type: 'warning',
      category: 'services',
      message: 'Application project not found, skipping service validation',
    });
    return;
  }

  // Find service implementations
  const serviceFiles = await findFiles('**/*Service.cs', {
    cwd: structure.application,
  });

  for (const file of serviceFiles) {
    const content = await readText(file);
    const fileName = path.basename(file, '.cs');

    // Skip interfaces
    if (fileName.startsWith('I')) continue;

    // Check if there's a matching interface
    const expectedInterface = `I${fileName}`;
    const interfacePattern = new RegExp(`:\\s*${expectedInterface}\\b`);

    if (!interfacePattern.test(content)) {
      // Check if file declares interface
      const declaresInterface = content.includes(`interface ${expectedInterface}`);

      if (!declaresInterface) {
        result.warnings.push({
          type: 'warning',
          category: 'services',
          message: `Service "${fileName}" should implement "${expectedInterface}"`,
          file: path.relative(structure.root, file),
          suggestion: `Create interface ${expectedInterface} and implement it`,
        });
      }
    }
  }
}

async function validateNamespaces(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ValidationResult
): Promise<void> {
  const layers = [
    { path: structure.domain, expected: config.conventions.namespaces.domain, name: 'Domain' },
    { path: structure.application, expected: config.conventions.namespaces.application, name: 'Application' },
    { path: structure.infrastructure, expected: config.conventions.namespaces.infrastructure, name: 'Infrastructure' },
    { path: structure.api, expected: config.conventions.namespaces.api, name: 'Api' },
  ];

  for (const layer of layers) {
    if (!layer.path) continue;

    const csFiles = await findFiles('**/*.cs', { cwd: layer.path });

    for (const file of csFiles.slice(0, 10)) { // Sample first 10 files
      const content = await readText(file);
      const namespaceMatch = content.match(/namespace\s+([\w.]+)/);

      if (namespaceMatch) {
        const namespace = namespaceMatch[1];

        if (!namespace.startsWith(layer.expected)) {
          result.errors.push({
            type: 'error',
            category: 'namespaces',
            message: `${layer.name} file has incorrect namespace "${namespace}"`,
            file: path.relative(structure.root, file),
            suggestion: `Should start with "${layer.expected}"`,
          });
        }
      }
    }
  }
}

async function validateEntities(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.domain) {
    result.warnings.push({
      type: 'warning',
      category: 'entities',
      message: 'Domain project not found, skipping entity validation',
    });
    return;
  }

  // Find entity files
  const entityFiles = await findFiles('**/*.cs', { cwd: structure.domain });

  for (const file of entityFiles) {
    const content = await readText(file);
    const fileName = path.basename(file, '.cs');

    // Skip non-entity files
    if (fileName.endsWith('Dto') || fileName.endsWith('Command') ||
        fileName.endsWith('Query') || fileName.endsWith('Handler') ||
        fileName.endsWith('Validator') || fileName.endsWith('Exception') ||
        fileName.startsWith('I')) {
      continue;
    }

    // Check for class declaration
    const classMatch = content.match(/public\s+(?:class|record)\s+(\w+)(?:\s*:\s*([^{]+))?/);
    if (!classMatch) continue;

    const entityName = classMatch[1];
    const inheritance = classMatch[2]?.trim() || '';

    // Check for proper base class/interface
    const hasBaseEntity = inheritance.includes('BaseEntity');
    const hasSystemEntity = inheritance.includes('SystemEntity');
    const hasITenantEntity = inheritance.includes('ITenantEntity');

    if (!hasBaseEntity && !hasSystemEntity) {
      // Could be a value object or other non-entity class
      continue;
    }

    // Entity should implement ITenantEntity if it's a BaseEntity (unless it's SystemEntity)
    if (hasBaseEntity && !hasSystemEntity && !hasITenantEntity) {
      result.warnings.push({
        type: 'warning',
        category: 'entities',
        message: `Entity "${entityName}" inherits BaseEntity but doesn't implement ITenantEntity`,
        file: path.relative(structure.root, file),
        suggestion: 'Add ITenantEntity interface for multi-tenant support, or use SystemEntity for platform-level entities',
      });
    }

    // Check for private constructor (DDD pattern)
    if (!content.includes(`private ${entityName}()`)) {
      result.warnings.push({
        type: 'warning',
        category: 'entities',
        message: `Entity "${entityName}" is missing private constructor for EF Core`,
        file: path.relative(structure.root, file),
        suggestion: `Add: private ${entityName}() { }`,
      });
    }

    // Check for factory method
    if (!content.includes(`public static ${entityName} Create(`)) {
      result.warnings.push({
        type: 'warning',
        category: 'entities',
        message: `Entity "${entityName}" is missing factory method`,
        file: path.relative(structure.root, file),
        suggestion: `Add factory method: public static ${entityName} Create(...)`,
      });
    }
  }
}

async function validateTenantAwareness(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.domain) {
    result.warnings.push({
      type: 'warning',
      category: 'tenants',
      message: 'Domain project not found, skipping tenant validation',
    });
    return;
  }

  // Find entity files
  const entityFiles = await findFiles('**/*.cs', { cwd: structure.domain });

  let tenantAwareCount = 0;
  let systemEntityCount = 0;
  let ambiguousCount = 0;

  for (const file of entityFiles) {
    const content = await readText(file);

    // Check for class declaration with inheritance
    const classMatch = content.match(/public\s+(?:class|record)\s+(\w+)(?:\s*:\s*([^{]+))?/);
    if (!classMatch) continue;

    const entityName = classMatch[1];
    const inheritance = classMatch[2]?.trim() || '';

    // Skip non-entity files
    if (!inheritance.includes('Entity') && !inheritance.includes('ITenant')) {
      continue;
    }

    const hasITenantEntity = inheritance.includes('ITenantEntity');
    const hasSystemEntity = inheritance.includes('SystemEntity');
    const hasTenantId = content.includes('TenantId');
    const hasTenantIdProperty = content.includes('public Guid TenantId') ||
                                 content.includes('public required Guid TenantId');

    // Check for ITenantEntity interface consistency
    if (hasITenantEntity) {
      tenantAwareCount++;

      if (!hasTenantIdProperty) {
        result.errors.push({
          type: 'error',
          category: 'tenants',
          message: `Entity "${entityName}" implements ITenantEntity but is missing TenantId property`,
          file: path.relative(structure.root, file),
          suggestion: 'Add: public Guid TenantId { get; private set; }',
        });
      }

      // Check Create method requires tenantId
      const createMethod = content.match(/public static \w+ Create\s*\(([^)]*)\)/);
      if (createMethod && !createMethod[1].includes('tenantId') && !createMethod[1].includes('TenantId')) {
        result.errors.push({
          type: 'error',
          category: 'tenants',
          message: `Entity "${entityName}" implements ITenantEntity but Create() doesn't require tenantId`,
          file: path.relative(structure.root, file),
          suggestion: 'Add tenantId as first parameter: Create(Guid tenantId, ...)',
        });
      }
    }

    // Check for SystemEntity consistency
    if (hasSystemEntity) {
      systemEntityCount++;

      if (hasTenantId) {
        result.errors.push({
          type: 'error',
          category: 'tenants',
          message: `System entity "${entityName}" should not have TenantId`,
          file: path.relative(structure.root, file),
          suggestion: 'Remove TenantId from system entities',
        });
      }
    }

    // Check for ambiguous tenant status
    if (!hasITenantEntity && !hasSystemEntity && hasTenantId) {
      ambiguousCount++;
      result.warnings.push({
        type: 'warning',
        category: 'tenants',
        message: `Entity "${entityName}" has TenantId but doesn't implement ITenantEntity`,
        file: path.relative(structure.root, file),
        suggestion: 'Add ITenantEntity interface for explicit tenant-awareness',
      });
    }
  }

  // Summary stats
  if (tenantAwareCount + systemEntityCount > 0) {
    result.warnings.push({
      type: 'warning',
      category: 'tenants',
      message: `Tenant summary: ${tenantAwareCount} tenant-aware, ${systemEntityCount} system, ${ambiguousCount} ambiguous`,
    });
  }
}

async function validateControllerRoutes(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.api) {
    result.warnings.push({
      type: 'warning',
      category: 'controllers',
      message: 'API project not found, skipping controller route validation',
    });
    return;
  }

  // Find controller files
  const controllerFiles = await findFiles('**/Controllers/**/*Controller.cs', {
    cwd: structure.api,
  });

  // System controllers that intentionally use hardcoded routes
  // These are either auth-related, user-level, or support module APIs not in the navigation hierarchy
  const systemControllers = [
    // Auth controllers
    'AuthController',
    'RegistrationController',
    'OnboardingController',
    'NavigationController',
    'LogsController',
    'EntraController',
    // User-level controllers (not in navigation hierarchy)
    'PreferencesController',
    'ApplicationTrackingController',
    // Support module controllers (using api/support/* routes)
    'NotificationsController',
    'SlaController',
    'TemplatesController',
    'TicketsController',
    // DashboardController exists in both User and Support - both use hardcoded routes
    'DashboardController',
    // Structure controllers (using api/structure/* routes - navigation paths don't exist yet)
    'CostCentersController',
    'OrganisationsController',
    'OrganizationalDomainsController',
    // Business controllers (using api/business/* routes - navigation paths don't exist yet)
    'MyTicketsController',
  ];

  let navRouteCount = 0;
  let hardcodedRouteCount = 0;
  let systemControllerCount = 0;

  for (const file of controllerFiles) {
    const content = await readText(file);
    const fileName = path.basename(file, '.cs');

    // Skip system controllers
    if (systemControllers.includes(fileName)) {
      systemControllerCount++;
      continue;
    }

    // Check for NavRoute attribute
    const hasNavRoute = content.includes('[NavRoute(');

    // Check for hardcoded Route attribute
    const hasHardcodedRoute = content.includes('[Route("api/[controller]")]') ||
                              content.includes('[Route("api/') ||
                              /\[Route\s*\(\s*"[^"]+"\s*\)\]/.test(content);

    if (hasNavRoute) {
      navRouteCount++;

      // Validate NavRoute format
      const navRouteMatch = content.match(/\[NavRoute\s*\(\s*"([^"]+)"(?:\s*,\s*Suffix\s*=\s*"([^"]+)")?\s*\)\]/);
      if (navRouteMatch) {
        const routePath = navRouteMatch[1];
        const parts = routePath.split('.');

        // Validate route has at least 2 parts (context.application or more)
        if (parts.length < 2) {
          result.warnings.push({
            type: 'warning',
            category: 'controllers',
            message: `Controller "${fileName}" has NavRoute with insufficient depth: "${routePath}"`,
            file: path.relative(structure.root, file),
            suggestion: 'NavRoute should have at least 2 levels: "context.application" (e.g., "platform.administration")',
          });
        }

        // Validate route parts are lowercase
        const hasUppercase = parts.some(part => part !== part.toLowerCase());
        if (hasUppercase) {
          result.errors.push({
            type: 'error',
            category: 'controllers',
            message: `Controller "${fileName}" has NavRoute with uppercase characters: "${routePath}"`,
            file: path.relative(structure.root, file),
            suggestion: 'NavRoute paths must be lowercase (e.g., "platform.administration.users")',
          });
        }
      }
    } else if (hasHardcodedRoute) {
      hardcodedRouteCount++;
      result.warnings.push({
        type: 'warning',
        category: 'controllers',
        message: `Controller "${fileName}" uses hardcoded Route instead of NavRoute`,
        file: path.relative(structure.root, file),
        suggestion: 'Use [NavRoute("context.application.module")] for navigation-based routing',
      });
    }
  }

  // Summary stats
  const totalControllers = controllerFiles.length;
  const businessControllers = totalControllers - systemControllerCount;
  const navRoutePercentage = businessControllers > 0
    ? Math.round((navRouteCount / businessControllers) * 100)
    : 0;

  result.warnings.push({
    type: 'warning',
    category: 'controllers',
    message: `Route summary: ${navRouteCount}/${businessControllers} business controllers use NavRoute (${navRoutePercentage}%), ${systemControllerCount} system controllers excluded`,
  });

  // If less than 80% use NavRoute, add a warning
  if (navRoutePercentage < 80 && businessControllers > 0) {
    result.warnings.push({
      type: 'warning',
      category: 'controllers',
      message: `NavRoute adoption is below 80% (${navRoutePercentage}%). Consider migrating remaining controllers.`,
    });
  }
}

async function validateLayouts(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.web) {
    result.warnings.push({
      type: 'warning',
      category: 'layouts',
      message: 'Web project not found, skipping layout validation',
    });
    return;
  }

  const layoutFiles = await findFiles('**/layouts/**/*.tsx', { cwd: structure.web });

  if (layoutFiles.length === 0) {
    result.warnings.push({
      type: 'warning',
      category: 'layouts',
      message: 'No layout files found in web/src/layouts/',
    });
    return;
  }

  for (const file of layoutFiles) {
    const content = await readText(file);
    const fileName = path.basename(file);
    const lines = content.split('\n');

    // Check for max-w-* in className attributes (line by line to avoid ReDoS)
    let hasMaxWidth = false;
    for (const line of lines) {
      // Skip comments
      if (line.trim().startsWith('//') || line.trim().startsWith('*')) continue;
      // Check for max-w-* in className (bounded pattern)
      if (/className.*max-w-/.test(line)) {
        hasMaxWidth = true;
        break;
      }
    }

    if (hasMaxWidth) {
      result.errors.push({
        type: 'error',
        category: 'layouts',
        message: `Layout "${fileName}" uses max-w-* constraint which limits content width`,
        file: path.relative(structure.root, file),
        suggestion: 'Remove max-w-* class. Content should occupy full available width.',
      });
    }

    // Check for standard padding pattern lg:px-10 in className
    let hasStandardPadding = false;
    for (const line of lines) {
      if (line.trim().startsWith('//') || line.trim().startsWith('*')) continue;
      if (/className.*lg:px-10/.test(line)) {
        hasStandardPadding = true;
        break;
      }
    }

    if (!hasStandardPadding) {
      result.warnings.push({
        type: 'warning',
        category: 'layouts',
        message: `Layout "${fileName}" may be missing standard horizontal padding`,
        file: path.relative(structure.root, file),
        suggestion: 'Use lg:px-10 for consistent horizontal padding across layouts',
      });
    }

    // Check for h-full overflow-auto pattern in className
    let hasScrollPattern = false;
    for (const line of lines) {
      if (line.trim().startsWith('//') || line.trim().startsWith('*')) continue;
      if (/className.*h-full/.test(line) && /className.*overflow-auto/.test(line)) {
        hasScrollPattern = true;
        break;
      }
    }

    if (!hasScrollPattern) {
      result.warnings.push({
        type: 'warning',
        category: 'layouts',
        message: `Layout "${fileName}" may be missing scroll container pattern`,
        file: path.relative(structure.root, file),
        suggestion: 'Use h-full overflow-auto for proper internal scrolling',
      });
    }
  }
}

async function validateTabs(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.web) {
    result.warnings.push({
      type: 'warning',
      category: 'tabs',
      message: 'Web project not found, skipping tab validation',
    });
    return;
  }

  const pageFiles = await findFiles('**/pages/**/*.tsx', { cwd: structure.web });

  let tabPagesCount = 0;
  let hookUsageCount = 0;
  let lazyLoadingCount = 0;

  for (const file of pageFiles) {
    const content = await readText(file);
    const fileName = path.basename(file);
    const lines = content.split('\n');

    // Detect tab patterns in actual code (skip comments)
    let hasTabPattern = false;
    for (const line of lines) {
      const trimmed = line.trim();
      // Skip comments and empty lines
      if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*') || trimmed === '') {
        continue;
      }
      // Check for tab component usage (JSX) or state management
      if (/<Tabs|<TabList|activeTab\s*[,=})]|setActiveTab\s*[({]/.test(line)) {
        hasTabPattern = true;
        break;
      }
    }

    if (hasTabPattern) {
      tabPagesCount++;

      // Check for useTabNavigation hook usage
      const usesHook = content.includes('useTabNavigation');

      if (usesHook) {
        hookUsageCount++;
      } else {
        result.errors.push({
          type: 'error',
          category: 'tabs',
          message: `Page "${fileName}" has tabs but doesn't use useTabNavigation hook`,
          file: path.relative(structure.root, file),
          suggestion: 'Use useTabNavigation hook to sync tab state with URL: const { activeTab, setActiveTab } = useTabNavigation(defaultTab, VALID_TABS)',
        });
      }

      // Check for lazy loading patterns
      const lazyLoadingPatterns = detectTabLazyLoading(content);

      if (lazyLoadingPatterns.hasLazyLoading) {
        lazyLoadingCount++;
      } else if (lazyLoadingPatterns.hasDataFetching) {
        // Only warn if the page fetches data but doesn't use lazy loading
        result.warnings.push({
          type: 'warning',
          category: 'tabs',
          message: `Page "${fileName}" has tabs with data fetching but no lazy loading pattern detected`,
          file: path.relative(structure.root, file),
          suggestion: 'Use lazy loading for tab data: useQuery([...], fetchFn, { enabled: activeTab === "tabName" }) or React.lazy() for tab components',
        });
      }
    }
  }

  if (tabPagesCount > 0) {
    const summaryParts = [
      `${hookUsageCount}/${tabPagesCount} use useTabNavigation hook`,
    ];
    if (lazyLoadingCount > 0 || tabPagesCount > hookUsageCount) {
      summaryParts.push(`${lazyLoadingCount}/${tabPagesCount} use lazy loading`);
    }
    result.warnings.push({
      type: 'warning',
      category: 'tabs',
      message: `Tab summary: ${summaryParts.join(', ')}`,
    });
  }
}

/**
 * Detect lazy loading patterns for tab content
 * Returns whether lazy loading is used and whether data fetching is present
 */
function detectTabLazyLoading(content: string): {
  hasLazyLoading: boolean;
  hasDataFetching: boolean;
  patterns: string[];
} {
  const patterns: string[] = [];
  let hasLazyLoading = false;
  let hasDataFetching = false;

  // Pattern 1: useQuery with enabled: activeTab === 'xxx'
  // Matches: enabled: activeTab === 'settings'
  // Matches: enabled: activeTab === "settings"
  // Matches: enabled: tab === 'settings'
  // Use two-step detection: check for useQuery AND enabled condition with tab check
  const hasUseQuery = /useQuery\s*\(/.test(content);
  const hasEnabledTabCondition = /enabled\s*:\s*(?:activeTab|tab|currentTab)\s*===\s*['"][^'"]+['"]/.test(content);
  if (hasUseQuery && hasEnabledTabCondition) {
    hasLazyLoading = true;
    patterns.push('useQuery with enabled condition');
  }

  // Pattern 2: React.lazy() for component imports
  // Matches: React.lazy(() => import('./TabSettings'))
  // Matches: const TabSettings = lazy(() => import('./tabs/TabSettings'))
  const reactLazyPattern = /(?:React\.)?lazy\s*\(\s*\(\)\s*=>\s*import\s*\(/;
  if (reactLazyPattern.test(content)) {
    hasLazyLoading = true;
    patterns.push('React.lazy() imports');
  }

  // Pattern 3: Suspense with fallback (indicates lazy loading is configured)
  // Matches: <Suspense fallback={...}>
  const suspensePattern = /<Suspense\s+[^>]*fallback\s*=/;
  if (suspensePattern.test(content)) {
    hasLazyLoading = true;
    patterns.push('Suspense with fallback');
  }

  // Pattern 4: Conditional rendering based on activeTab with component
  // Matches: {activeTab === 'settings' && <SettingsTab />}
  // Matches: activeTab === 'info' ? <InfoTab /> : null
  const conditionalRenderPattern = /(?:activeTab|tab|currentTab)\s*===\s*['"][^'"]+['"]\s*(?:&&|\?)\s*<[A-Z]/;
  if (conditionalRenderPattern.test(content)) {
    hasLazyLoading = true;
    patterns.push('Conditional tab rendering');
  }

  // Pattern 5: useSWR with conditional fetching
  // Matches: useSWR(activeTab === 'xxx' ? key : null, ...)
  const useSWRConditionalPattern = /useSWR\s*\(\s*(?:activeTab|tab|currentTab)\s*===\s*['"][^'"]+['"]\s*\?/;
  if (useSWRConditionalPattern.test(content)) {
    hasLazyLoading = true;
    patterns.push('useSWR with conditional key');
  }

  // Detect if the page has data fetching at all (to determine if lazy loading is relevant)
  // Check for useQuery, useSWR, fetch, axios patterns
  const dataFetchingPatterns = [
    /useQuery\s*\(/,
    /useSWR\s*\(/,
    /useMutation\s*\(/,
    /fetch\s*\(/,
    /axios\./,
    /useEffect\s*\([^)]*\{[^}]*(?:fetch|axios|api\.)/s,
  ];

  for (const pattern of dataFetchingPatterns) {
    if (pattern.test(content)) {
      hasDataFetching = true;
      break;
    }
  }

  return {
    hasLazyLoading,
    hasDataFetching,
    patterns,
  };
}

async function validateHierarchies(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.domain) {
    result.warnings.push({
      type: 'warning',
      category: 'hierarchies',
      message: 'Domain project not found, skipping hierarchy validation',
    });
    return;
  }

  // Find entity files
  const entityFiles = await findFiles('**/*.cs', { cwd: structure.domain });

  interface HierarchyInfo {
    name: string;
    file: string;
    isSelfReferencing: boolean;
    selfRefField?: string;
    parentEntity?: string;
    isTenantAware: boolean;
    childCollections: string[];
    depth: number;
  }

  const hierarchies: HierarchyInfo[] = [];
  const entityNames = new Set<string>();

  // First pass: collect all entity names and detect hierarchies
  for (const file of entityFiles) {
    const content = await readText(file);
    const fileName = path.basename(file, '.cs');

    // Skip non-entity files
    if (
      fileName.endsWith('Dto') ||
      fileName.endsWith('Command') ||
      fileName.endsWith('Query') ||
      fileName.endsWith('Handler') ||
      fileName.endsWith('Validator') ||
      fileName.endsWith('Exception') ||
      fileName.startsWith('I')
    ) {
      continue;
    }

    // Check for class declaration
    const classMatch = content.match(
      /public\s+(?:class|record)\s+(\w+)(?:\s*:\s*([^{]+))?/
    );
    if (!classMatch) continue;

    const entityName = classMatch[1];
    const inheritance = classMatch[2]?.trim() || '';

    // Must be an entity
    const hasBaseEntity = inheritance.includes('BaseEntity');
    const hasSystemEntity = inheritance.includes('SystemEntity');
    if (!hasBaseEntity && !hasSystemEntity) continue;

    entityNames.add(entityName);
    const hasITenantEntity = inheritance.includes('ITenantEntity');

    // Detect self-referencing (ParentId of same type)
    const selfRefMatch = content.match(
      new RegExp(
        `public\\s+(?:Guid\\??|${entityName}\\??)\\s+(Parent(?:Id)?|Parent${entityName}(?:Id)?)\\s*\\{`,
        'i'
      )
    );

    // Detect child collections
    const childCollections: string[] = [];
    const collectionMatches = content.matchAll(
      /public\s+(?:virtual\s+)?ICollection<(\w+)>\s+(\w+)\s*\{/g
    );
    for (const match of collectionMatches) {
      childCollections.push(match[1]);
    }

    // Detect parent entity from FK
    const fkMatches = content.matchAll(/public\s+Guid\s+(\w+Id)\s*\{[^}]+\}/g);
    let parentEntity: string | undefined;
    for (const match of fkMatches) {
      const propName = match[1];
      if (propName.toLowerCase().includes('parent')) continue;
      if (propName === 'TenantId') continue;
      const refEntity = propName.replace(/Id$/, '');
      if (refEntity !== entityName) {
        parentEntity = refEntity;
        break;
      }
    }

    if (selfRefMatch || childCollections.length > 0 || parentEntity) {
      hierarchies.push({
        name: entityName,
        file: path.relative(structure.root, file),
        isSelfReferencing: !!selfRefMatch,
        selfRefField: selfRefMatch ? selfRefMatch[1] : undefined,
        parentEntity,
        isTenantAware: hasITenantEntity,
        childCollections,
        depth: 0, // Will be calculated later
      });
    }
  }

  // Validation checks
  let selfRefCount = 0;
  let parentChildCount = 0;
  let deepHierarchyCount = 0;

  for (const h of hierarchies) {
    if (h.isSelfReferencing) {
      selfRefCount++;

      // Check 1: Self-referencing entities should have index on ParentId
      result.warnings.push({
        type: 'warning',
        category: 'hierarchies',
        message: `Entity "${h.name}" is self-referencing via "${h.selfRefField}"`,
        file: h.file,
        suggestion: `Consider using TVF with CTE for efficient hierarchy traversal. Ensure index on ${h.selfRefField}.`,
      });

      // Check 2: Self-referencing permission/group entities - critical for TVF
      if (
        h.name.toLowerCase().includes('group') ||
        h.name.toLowerCase().includes('permission') ||
        h.name.toLowerCase().includes('role')
      ) {
        result.warnings.push({
          type: 'warning',
          category: 'hierarchies',
          message: `Permission/Group entity "${h.name}" detected with self-reference`,
          file: h.file,
          suggestion: `CRITICAL: Use fn_Get${h.name}Hierarchy TVF for recursive permission resolution. Run analyze_hierarchy_patterns for SQL template.`,
        });
      }
    }

    if (h.parentEntity) {
      parentChildCount++;

      // Check 3: Parent entity should exist
      if (!entityNames.has(h.parentEntity)) {
        result.errors.push({
          type: 'error',
          category: 'hierarchies',
          message: `Entity "${h.name}" references non-existent parent "${h.parentEntity}"`,
          file: h.file,
          suggestion: `Create the ${h.parentEntity} entity or fix the foreign key reference.`,
        });
      }
    }

    // Check 4: Tenant awareness consistency
    if (h.parentEntity && entityNames.has(h.parentEntity)) {
      const parentInfo = hierarchies.find((x) => x.name === h.parentEntity);
      if (parentInfo && parentInfo.isTenantAware && !h.isTenantAware) {
        result.errors.push({
          type: 'error',
          category: 'hierarchies',
          message: `Entity "${h.name}" has tenant-aware parent "${h.parentEntity}" but is not tenant-aware itself`,
          file: h.file,
          suggestion: `Add ITenantEntity interface to ${h.name} for consistent tenant isolation.`,
        });
      }
    }

    // Check 5: Deep hierarchies (estimated > 3 levels)
    if (h.childCollections.length > 0) {
      for (const child of h.childCollections) {
        const childInfo = hierarchies.find((x) => x.name === child);
        if (childInfo && childInfo.childCollections.length > 0) {
          deepHierarchyCount++;
          result.warnings.push({
            type: 'warning',
            category: 'hierarchies',
            message: `Deep hierarchy detected: ${h.name} → ${child} → ...`,
            file: h.file,
            suggestion: `Consider TVF for efficient traversal. Run analyze_hierarchy_patterns for recommendations.`,
          });
        }
      }
    }
  }

  // Summary
  if (hierarchies.length > 0) {
    result.warnings.push({
      type: 'warning',
      category: 'hierarchies',
      message: `Hierarchy summary: ${selfRefCount} self-referencing, ${parentChildCount} parent-child, ${deepHierarchyCount} deep hierarchies detected`,
    });
  }
}

async function validateProtectedActions(
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  _config: Config,
  result: ValidationResult
): Promise<void> {
  if (!structure.web) {
    result.warnings.push({
      type: 'warning',
      category: 'protected-actions',
      message: 'Web project not found, skipping protected actions validation',
    });
    return;
  }

  // Find all TSX files in pages and components directories
  const pageFiles = await findFiles('**/pages/**/*.tsx', { cwd: structure.web });
  const componentFiles = await findFiles('**/components/**/*.tsx', { cwd: structure.web });
  const allFiles = [...pageFiles, ...componentFiles];

  // CRUD action patterns to detect in button labels or onClick handlers
  const actionPatterns = {
    create: ['create', 'add', 'new', 'créer', 'ajouter', 'nouveau'],
    update: ['edit', 'update', 'modify', 'modifier', 'éditer', 'save', 'enregistrer'],
    delete: ['delete', 'remove', 'supprimer', 'retirer'],
    execute: ['execute', 'run', 'sync', 'exécuter', 'lancer', 'synchroniser'],
  };

  let protectedButtonCount = 0;
  let hasPermissionCount = 0;
  let unprotectedCount = 0;
  const unprotectedButtons: { file: string; line: number; action: string; code: string }[] = [];

  for (const file of allFiles) {
    const content = await readText(file);
    const lines = content.split('\n');

    // Skip if file uses ProtectedButton (already following convention)
    const usesProtectedButton = content.includes('<ProtectedButton');
    if (usesProtectedButton) {
      protectedButtonCount++;
      continue;
    }

    // Scan for button elements with CRUD-like actions
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Skip comments
      if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*')) {
        continue;
      }

      // Check for button patterns: <button, <Button, onClick with action handlers
      const buttonMatch = /<(?:button|Button)[^>]*>([^<]*)<\/|onClick\s*=\s*\{[^}]*handle(Create|Delete|Edit|Update|Remove|Save|Add|Sync|Execute)/i.exec(line);

      if (buttonMatch) {
        const buttonContent = buttonMatch[1]?.toLowerCase() || '';
        const handlerAction = buttonMatch[2]?.toLowerCase() || '';

        // Detect which action type this button represents
        let detectedAction: string | null = null;

        for (const [action, keywords] of Object.entries(actionPatterns)) {
          if (keywords.some(kw => buttonContent.includes(kw) || handlerAction.includes(kw))) {
            detectedAction = action;
            break;
          }
        }

        if (detectedAction) {
          // Check if this button has permission protection
          // Look at surrounding context (5 lines before and after)
          const startLine = Math.max(0, i - 5);
          const endLine = Math.min(lines.length - 1, i + 5);
          const context = lines.slice(startLine, endLine + 1).join('\n');

          const hasPermissionCheck =
            context.includes('hasPermission') ||
            context.includes('canCreate') ||
            context.includes('canEdit') ||
            context.includes('canUpdate') ||
            context.includes('canDelete') ||
            context.includes('canExecute') ||
            context.includes('disabled={!can');

          if (!hasPermissionCheck) {
            unprotectedCount++;
            unprotectedButtons.push({
              file: path.relative(structure.root, file),
              line: i + 1,
              action: detectedAction,
              code: line.trim().substring(0, 80),
            });
          } else {
            hasPermissionCount++;
          }
        }
      }
    }
  }

  // Report unprotected buttons (limit to first 10 for readability)
  const reportedButtons = unprotectedButtons.slice(0, 10);
  for (const btn of reportedButtons) {
    result.warnings.push({
      type: 'warning',
      category: 'protected-actions',
      message: `Button with "${btn.action}" action may be missing permission check`,
      file: btn.file,
      line: btn.line,
      suggestion: `Use <ProtectedButton action="${btn.action}" navRoute="..."> or add hasPermission check`,
    });
  }

  if (unprotectedButtons.length > 10) {
    result.warnings.push({
      type: 'warning',
      category: 'protected-actions',
      message: `... and ${unprotectedButtons.length - 10} more unprotected buttons`,
    });
  }

  // Summary
  const totalActionButtons = protectedButtonCount + hasPermissionCount + unprotectedCount;
  if (totalActionButtons > 0) {
    const protectedPercentage = Math.round(
      ((protectedButtonCount + hasPermissionCount) / totalActionButtons) * 100
    );
    result.warnings.push({
      type: 'warning',
      category: 'protected-actions',
      message: `Protected actions summary: ${protectedButtonCount} use ProtectedButton, ${hasPermissionCount} use hasPermission, ${unprotectedCount} potentially unprotected (${protectedPercentage}% protected)`,
    });

    if (protectedPercentage < 80 && totalActionButtons > 5) {
      result.errors.push({
        type: 'error',
        category: 'protected-actions',
        message: `Less than 80% of action buttons are protected (${protectedPercentage}%)`,
        suggestion: 'Migrate action buttons to use <ProtectedButton> component for consistent permission checking',
      });
    }
  }
}

function generateSummary(result: ValidationResult, checks: string[]): string {
  const parts: string[] = [];

  parts.push(`Checks performed: ${checks.join(', ')}`);
  parts.push(`Errors: ${result.errors.length}`);
  parts.push(`Warnings: ${result.warnings.length}`);
  parts.push(`Status: ${result.valid ? 'PASSED' : 'FAILED'}`);

  return parts.join(' | ');
}

function formatResult(result: ValidationResult): string {
  const lines: string[] = [];

  lines.push('# Conventions Validation Report');
  lines.push('');
  lines.push(`## Summary`);
  lines.push(`- **Status**: ${result.valid ? '✅ PASSED' : '❌ FAILED'}`);
  lines.push(`- **Errors**: ${result.errors.length}`);
  lines.push(`- **Warnings**: ${result.warnings.length}`);
  lines.push('');

  if (result.errors.length > 0) {
    lines.push('## Errors');
    lines.push('');
    for (const error of result.errors) {
      lines.push(`### ❌ ${error.category}: ${error.message}`);
      if (error.file) lines.push(`- **File**: \`${error.file}\``);
      if (error.line) lines.push(`- **Line**: ${error.line}`);
      if (error.suggestion) lines.push(`- **Suggestion**: ${error.suggestion}`);
      lines.push('');
    }
  }

  if (result.warnings.length > 0) {
    lines.push('## Warnings');
    lines.push('');
    for (const warning of result.warnings) {
      lines.push(`### ⚠️ ${warning.category}: ${warning.message}`);
      if (warning.file) lines.push(`- **File**: \`${warning.file}\``);
      if (warning.suggestion) lines.push(`- **Suggestion**: ${warning.suggestion}`);
      lines.push('');
    }
  }

  if (result.errors.length === 0 && result.warnings.length === 0) {
    lines.push('## Result');
    lines.push('');
    lines.push('All conventions validated successfully! ✨');
  }

  return lines.join('\n');
}
